import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { formatIST } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { LANGUAGES, changeLanguage } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import api from '@/lib/api';
import { buildNavItems, groupNavItems, navMnemonic } from '@/lib/navigation';
import {
  useSidebarWidth,
  SIDEBAR_COLLAPSED_WIDTH,
  SIDEBAR_MAX_WIDTH,
} from '@/hooks/useSidebarWidth';
import { MnemonicScope } from '@/contexts/MnemonicContext';
import { SCOPE_PAGE } from '@/lib/mnemonics/registry';
import NavLinkWithMnemonic from '@/components/mnemonics/NavLinkWithMnemonic';
import {
  Package, Users, UserCog, Warehouse, Truck, Settings, Bell, LogOut, ChevronLeft,
  Menu, ClipboardList, Scissors, Globe, FileText, Cog, Trash2, BarChart3, Shield, Boxes,
  FileSpreadsheet, Receipt
} from 'lucide-react';

const LOGO_URL = "/logo.png";
// the wordmark is 3.2:1, so it disappears in a collapsed rail — that shows the mark
const MARK_URL = "/mark.png";

const ORDERS = { labelKey: "nav.orders", label: "Orders", path: "/orders", icon: Package };
const JOB_CARDS = { labelKey: "nav.jobCards", label: "Job Cards", path: "/job-cards", icon: FileText };
const PLATES = { labelKey: "nav.plates", label: "Plate Tracking", path: "/plates", icon: ClipboardList };
const DIES = { labelKey: "nav.dies", label: "Die Tracking", path: "/dies", icon: Scissors };
const PLATE_REGISTER = { labelKey: "nav.plateRegister", label: "Plate Register", path: "/plate-register", icon: Boxes };
const DIE_REGISTER = { labelKey: "nav.dieRegister", label: "Die Register", path: "/die-register", icon: Boxes };
const INVENTORY = { labelKey: "nav.inventory", label: "Inventory", path: "/inventory", icon: Warehouse };
const CUSTOMERS = { labelKey: "nav.customers", label: "Customers", path: "/customers", icon: Users };
const QUOTATIONS = { labelKey: "nav.quotations", label: "Quotations", path: "/quotations", icon: FileSpreadsheet };
const PROFORMA_INVOICES = { labelKey: "nav.proformaInvoices", label: "Proforma Invoices", path: "/proforma-invoices", icon: Receipt };
const DISPATCH = { labelKey: "nav.dispatch", label: "Dispatch", path: "/dispatch", icon: Truck };
const REPORTS = { labelKey: "nav.reports", label: "Reports", path: "/reports", icon: BarChart3 };
const USERS = { labelKey: "nav.users", label: "Users", path: "/users", icon: UserCog };
const RECYCLE_BIN = { labelKey: "nav.recycleBin", label: "Recycle Bin", path: "/recycle-bin", icon: Trash2 };
const MACHINES = { labelKey: "nav.machines", label: "Machines", path: "/machines", icon: Cog };
const SETTINGS = { labelKey: "nav.settings", label: "Settings", path: "/admin-settings", icon: Settings };
const NETWORK_ACCESS = { labelKey: "nav.networkAccess", label: "Network Access", path: "/network-access", icon: Shield };

// A role is only given a destination its route will actually admit it to —
// anything else bounces off the guard and back to the role's landing page.
const ROLE_NAV = {
  admin: [
    ORDERS, QUOTATIONS, PROFORMA_INVOICES, JOB_CARDS, PLATES, DIES, INVENTORY,
    PLATE_REGISTER, DIE_REGISTER, CUSTOMERS, DISPATCH, REPORTS,
    USERS, RECYCLE_BIN, MACHINES, SETTINGS, NETWORK_ACCESS,
  ],
  receptionist: [ORDERS, QUOTATIONS, PROFORMA_INVOICES, JOB_CARDS, PLATES, DIES, INVENTORY, PLATE_REGISTER, DIE_REGISTER, CUSTOMERS, DISPATCH, USERS],
  designer: [ORDERS, JOB_CARDS, PLATES, DIES, PLATE_REGISTER, DIE_REGISTER, CUSTOMERS, USERS],
  accountant: [ORDERS, QUOTATIONS, PROFORMA_INVOICES, CUSTOMERS, DISPATCH, REPORTS],
  store_keeper: [ORDERS, INVENTORY, PLATE_REGISTER, DIE_REGISTER],
  dispatcher: [ORDERS, DISPATCH, REPORTS],
  city_office_manager: [ORDERS, QUOTATIONS, PROFORMA_INVOICES, CUSTOMERS, DISPATCH, REPORTS],
  supervisor: [ORDERS, JOB_CARDS, PLATES, DIES, PLATE_REGISTER, DIE_REGISTER, CUSTOMERS, REPORTS],
  // The shop floor worked from its own queue, which went with the mobile build.
  // Orders is the one page these roles are admitted to.
  machine: [ORDERS],
  machine_man: [ORDERS],  // legacy accounts created before the role was renamed
  binder: [ORDERS],
};

const ROLE_LABELS = {
  admin: "Admin",
  receptionist: "Receptionist",
  head_designer: "Head Designer",  // post open; kept so an old account still reads right
  designer: "Designer",
  accountant: "Accountant",
  store_keeper: "Store Keeper",
  dispatcher: "Dispatcher",
  city_office_manager: "City Office Mgr",
  supervisor: "Supervisor",
  machine: "Machine",
  binder: "Binder",
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { width: sidebarWidth, collapsed, resizing, toggleCollapsed, resizeHandleProps } = useSidebarWidth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const mainRef = useRef(null);
  const isFirstRoute = useRef(true);

  // On navigation, move focus into the content region. Without this, focus stays
  // on the sidebar link and Tab restarts from the nav on every page.
  useEffect(() => {
    if (isFirstRoute.current) {
      isFirstRoute.current = false;
      return;
    }
    mainRef.current?.focus();
  }, [location.pathname]);

  const navItems = useMemo(() => buildNavItems(user, ROLE_NAV), [user]);

  // A flat list is one you read; the lanes make it a menu you aim at.
  const navGroups = useMemo(() => groupNavItems(navItems), [navItems]);

  const fetchNotifications = useCallback(async () => {
    try {
      const [countRes, listRes] = await Promise.all([
        api.get('/notifications/unread-count'),
        api.get('/notifications?limit=10')
      ]);
      setUnreadCount(countRes.data.count);
      setNotifications(listRes.data.notifications);
    } catch {}
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAllRead = async () => {
    try {
      await api.put('/notifications/read-all');
      setUnreadCount(0);
      setNotifications(n => n.map(x => ({ ...x, read: true })));
    } catch {}
  };

  const handleLogout = () => {
    setLogoutDialogOpen(false);
    logout();
    navigate('/login');
  };

  const confirmLogout = () => {
    setLogoutDialogOpen(true);
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background" data-testid="main-layout">
      <a href="#main-content" className="skip-link">Skip to content</a>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-50 flex flex-col bg-white border-r border-slate-200/80 w-64 lg:w-[var(--sidebar-w)]
          ${resizing ? 'transition-none' : 'transition-all duration-200'} ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
        style={{ '--sidebar-w': `${sidebarWidth}px` }}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className={`flex items-center h-16 border-b border-slate-100 ${collapsed ? 'justify-center px-2' : 'px-4'}`}>
          <img src={collapsed ? MARK_URL : LOGO_URL} alt="Labelify Private Limited" className={collapsed ? "h-8 w-8 object-contain" : "h-10 object-contain"} />
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 py-3">
          <nav className={collapsed ? 'px-2' : 'px-3'}>
            {navGroups.map((group) => {
              if (group.separator) {
                return (
                  <div key={group.id} className="py-2">
                    <div className={`border-t border-slate-200 ${!collapsed ? 'mx-2' : ''}`} />
                    {!collapsed && (
                      <p className="text-[10px] uppercase text-slate-400 mt-2 px-2 tracking-wider">Secondary</p>
                    )}
                  </div>
                );
              }

              return (
                <div key={group.id} className="mb-2 last:mb-0">
                  {!collapsed && (
                    <p className="px-2 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                      {group.label}
                    </p>
                  )}
                  <div className="space-y-0.5">
                    {group.items.map((item) => {
                      const displayLabel = item.labelKey
                        ? (t(item.labelKey) !== item.labelKey ? t(item.labelKey) : item.label)
                        : item.label;
                      const isActive = location.pathname === item.path ||
                        (item.path !== '/' && location.pathname.startsWith(item.path));
                      return (
                        <NavLinkWithMnemonic
                          key={item.path}
                          to={item.path}
                          mnemonic={navMnemonic(item.path)}
                          label={displayLabel}
                          icon={item.icon}
                          collapsed={collapsed}
                          isActive={isActive}
                          onNavigate={() => setMobileOpen(false)}
                          data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </nav>
        </ScrollArea>

        {/* Collapse toggle */}
        <div className="border-t border-slate-100 p-2">
          <button
            onClick={toggleCollapsed}
            className="hidden lg:flex items-center justify-center w-full p-2 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
            data-testid="sidebar-collapse-btn"
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <ChevronLeft className={`h-5 w-5 transition-transform ${collapsed ? 'rotate-180' : ''}`} />
          </button>
        </div>
      </aside>

      {/* Resize handle — a flex sibling rather than an overlay, so the strip it
          occupies is the strip you can grab */}
      <div
        {...resizeHandleProps}
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize sidebar"
        aria-valuenow={sidebarWidth}
        aria-valuemin={SIDEBAR_COLLAPSED_WIDTH}
        aria-valuemax={SIDEBAR_MAX_WIDTH}
        tabIndex={0}
        title="Drag to resize, double-click to reset"
        className="group hidden lg:flex w-1.5 shrink-0 cursor-col-resize touch-none items-stretch justify-center bg-slate-50 hover:bg-slate-100 focus-visible:bg-slate-100"
        data-testid="sidebar-resize-handle"
      >
        <span
          className={`w-px transition-colors ${resizing ? 'bg-brand-blue' : 'bg-transparent group-hover:bg-brand-blue/40'}`}
        />
      </div>

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        {/* One line, 36px. It used to be 64px carrying a two-line greeting and
            the user's name twice — the page below it is the work, and every
            page already titles itself. */}
        <header className="flex h-11 shrink-0 items-center justify-between border-b border-slate-200/80 bg-white px-3 sm:h-9 md:px-4" data-testid="header">
          <div className="flex min-w-0 items-center gap-2">
            <button
              className="lg:hidden -ml-1 rounded p-2 hover:bg-slate-100 sm:p-1"
              onClick={() => setMobileOpen(true)}
              aria-label="Open navigation"
              data-testid="mobile-menu-btn"
            >
              <Menu className="h-5 w-5 sm:h-4 sm:w-4" />
            </button>
            <span className="hidden min-w-0 items-center gap-1.5 sm:flex">
              <span className="truncate text-xs font-semibold text-slate-800">{user?.full_name}</span>
              <span className="shrink-0 text-[11px] text-slate-400">
                {ROLE_LABELS[user?.role] || user?.role}
                {user?.secondary_roles?.length > 0 && ` +${user.secondary_roles.length}`}
              </span>
            </span>
          </div>

          <div className="flex items-center gap-1">
            {/* Notifications */}
            <Popover open={notifOpen} onOpenChange={setNotifOpen}>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="relative h-9 w-9 sm:h-7 sm:w-7" data-testid="notification-bell">
                  <Bell className="h-4 w-4 text-slate-600" />
                  {unreadCount > 0 && (
                    <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-brand-gold text-[9px] font-bold text-brand-navy">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[min(20rem,calc(100vw-1.5rem))] p-0" align="end">
                <div className="flex items-center justify-between p-3 border-b">
                  <h3 className="font-heading font-semibold text-sm">Notifications</h3>
                  {unreadCount > 0 && (
                    <Button variant="ghost" size="sm" onClick={markAllRead} className="text-xs text-brand-blue" data-testid="mark-all-read-btn">
                      Mark all read
                    </Button>
                  )}
                </div>
                <ScrollArea className="max-h-72">
                  {notifications.length === 0 ? (
                    <p className="p-4 text-sm text-slate-500 text-center">No notifications</p>
                  ) : (
                    notifications.map((n) => (
                      <div
                        key={n.id}
                        className={`p-3 border-b last:border-0 cursor-pointer hover:bg-slate-50 transition-colors ${!n.read ? 'bg-blue-50/50' : ''}`}
                        onClick={() => {
                          if (n.entity_type === 'order') navigate(`/orders/${n.entity_id}`);
                          setNotifOpen(false);
                        }}
                      >
                        <p className="text-sm text-slate-700">{n.message}</p>
                        <p className="text-xs text-slate-400 mt-1">
                          {formatIST(n.created_at, { compact: true })}
                        </p>
                      </div>
                    ))
                  )}
                </ScrollArea>
              </PopoverContent>
            </Popover>

            {/* Language Switcher */}
            <Select value={i18n.language} onValueChange={changeLanguage}>
              <SelectTrigger className="h-9 w-auto gap-1 border-0 px-2 text-xs shadow-none sm:h-7 sm:px-1.5" data-testid="language-switcher">
                <Globe className="h-3.5 w-3.5 text-slate-500" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LANGUAGES.map(lang => (
                  <SelectItem key={lang.code} value={lang.code}>{lang.nativeLabel}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* the name is on the left of this same bar; twice was once too many */}
            <Button variant="ghost" size="icon" className="h-9 w-9 border-l sm:h-7 sm:w-7" onClick={confirmLogout}
              data-testid="logout-btn" title="Logout">
              <LogOut className="h-4 w-4 text-slate-600" />
            </Button>
          </div>
        </header>

        {/* Page content — keyed on pathname so each route gets a fresh page
            scope and the previous route's mnemonics are torn down with it */}
        <main
          id="main-content"
          ref={mainRef}
          tabIndex={-1}
          className="flex-1 overflow-auto p-3 md:p-4 focus:outline-none"
        >
          <MnemonicScope key={location.pathname} type={SCOPE_PAGE} label={location.pathname}>
            {children}
          </MnemonicScope>
        </main>
      </div>

      {/* Logout Confirmation Dialog */}
      <Dialog open={logoutDialogOpen} onOpenChange={setLogoutDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Logout</DialogTitle>
            <DialogDescription>
              Are you sure you want to log out? Any unsaved changes will be lost.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLogoutDialogOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleLogout} data-testid="confirm-logout-btn">
              Log Out
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
